// @ts-nocheck — verbatim port from xop-edge-staff. See src/crm/README.md.
/**
 * Pure recipient-count + summary helpers for the email-campaign target selector,
 * extracted from `email-target-selector.tsx`. No money / reseller here —
 * wall-neutral.
 */
import { memberCount } from "@crm-src/pages/contact-groups/membership";

/** A contact group/list — the size fields the estimate reads (real `ContactList`
 * carries more; it satisfies this structurally). */
interface GroupLike {
  id: number;
  members_count?: number | null;
  contacts_count?: number | null;
}

/**
 * Estimated contacts reachable via the selected groups: the sum of each group's
 * `members_count` (falling back to `contacts_count` only when it is ABSENT —
 * see `memberCount`, which is why this is not `||`); an unknown group
 * contributes 0.
 */
export function sumGroupContacts(groupIds: number[], groups: GroupLike[]): number {
  return groupIds.reduce((sum, groupId) => {
    const group = groups.find((g) => g.id === groupId);
    return sum + (group ? memberCount(group) : 0);
  }, 0);
}

/**
 * Estimated contacts reachable via the selected industry segment: the sum of the
 * per-industry counts for the chosen labels. `null` counts (not yet loaded)
 * yield 0.
 */
export function sumIndustryContacts(
  selectedIndustries: string[],
  industryCounts: Record<string, number> | null,
): number {
  if (!industryCounts) return 0;
  return selectedIndustries.reduce((sum, label) => sum + (industryCounts[label] || 0), 0);
}

export interface RecipientSummaryCounts {
  recipientCount: number;
  groupCount: number;
  estimatedGroupContacts: number;
  industryCount: number;
  estimatedIndustryContacts: number;
}

/**
 * The human-readable recipient summary shown in the section header, e.g.
 * `"2 individuals + 1 group (~40 contacts) + 3 industries (~120 contacts)"`.
 * The individuals part is ALWAYS present (even at 0); the group and industry
 * parts appear only when their count is non-zero. Pluralization: individual(s),
 * group(s), industry/industries.
 */
export function buildRecipientSummary(counts: RecipientSummaryCounts): string {
  const { recipientCount, groupCount, estimatedGroupContacts, industryCount, estimatedIndustryContacts } = counts;
  const parts = [`${recipientCount} individual${recipientCount !== 1 ? 's' : ''}`];
  if (groupCount > 0) {
    parts.push(`${groupCount} group${groupCount !== 1 ? 's' : ''} (~${estimatedGroupContacts} contacts)`);
  }
  if (industryCount > 0) {
    parts.push(
      `${industryCount} industr${industryCount !== 1 ? 'ies' : 'y'} (~${estimatedIndustryContacts} contacts)`,
    );
  }
  return parts.join(' + ');
}
